import api from '../../api/client';
import React, { useState, useEffect } from "react";
import {
  Card,
  Select,
  Table,
  Tag,
  Button,
  Space,
  message,
  Spin,
  Tooltip,
  Popconfirm,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { useSelector } from "react-redux";
import config from "../../config";
import { toast } from "react-toastify";

const { Option } = Select;

const LeaveRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const account = useSelector((state) => state.account.user);
  const userId = account.id;

  useEffect(() => {
    const fetchUserRoles = async () => {
      try {
        const { data } = await api.get(`/positions`);
        setUserRoles(data);
      } catch (error) {
        console.error("Failed to fetch roles", error);
      }
    };
    fetchUserRoles();
  }, [api]);

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/leave/requests?status=${statusFilter}`,
      );
      setRequests(data);
    } catch (error) {
      message.error("Failed to fetch leave requests");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (requestId, newStatus, leaveDays) => {
    try {
      const { data } = await api.put(
        `/leave/requests/${requestId}`,
        {
          status: newStatus,
          updated_by: userId,
          leave_day: leaveDays,
        },
      );
      toast.success(data.message);
      fetchRequests();
    } catch (err) {
      message.error("Failed to update status");
    }
  };

  const getRoleName = (positionId) => {
    const role = userRoles.find((r) => Number(r.id) === Number(positionId));
    return role?.name || "—";
  };

  const columns = [
    {
      title: "Employee",
      dataIndex: "firstname",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <span className="fw-bold">
            {record.firstname} {record.lastname}
          </span>
          <span className="text-muted small">
            {getRoleName(record.position)}
          </span>
        </Space>
      ),
      sorter: (a, b) => a.firstname.localeCompare(b.firstname),
    },
    {
      title: "Dates",
      render: (_, record) =>
        `${dayjs(record.start_date).format("MMM DD")} - ${dayjs(
          record.end_date,
        ).format("MMM DD")}`,
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (t) => <span className="text-capitalize">{t}</span>,
    },
    { title: "Days", dataIndex: "leave_days" },
    {
      title: "Request Time",
      dataIndex: "created_at",
      render: (d) => dayjs(d).format("MMM DD, YYYY"),
    },
    { title: "Reason", dataIndex: "reason", ellipsis: true },
    {
      title: "Balance",
      render: (_, record) => (
        <BalanceInfo userId={record.user_id} requestDays={record.leave_days} />
      ),
    },
    {
      title: "Actions",
      render: (_, record) =>
        record.status === "pending" ? (
          <Space>
            <Popconfirm
              title="Approve this leave request?"
              onConfirm={() =>
                handleStatusChange(record.id, "approved", record.leave_days)
              }
            >
              <Button type="primary" size="small" icon={<CheckOutlined />}>
                Approve
              </Button>
            </Popconfirm>
            <Popconfirm
              title="Reject this leave request?"
              onConfirm={() => handleStatusChange(record.id, "rejected")}
            >
              <Button danger size="small" icon={<CloseOutlined />}>
                Reject
              </Button>
            </Popconfirm>
          </Space>
        ) : (
          <Tag color={record.status === "approved" ? "green" : "red"}>
            {record.status.toUpperCase()}
          </Tag>
        ),
    },
  ];

  const exportToCSV = () => {
    if (!requests.length) return message.warning("No data to export");
    const headers = [
      "Employee",
      "Start Date",
      "End Date",
      "Type",
      "Days",
      "Reason",
      "Status",
      "Requested On",
    ];
    const rows = requests.map((r) => [
      `${r.firstname} ${r.lastname}`,
      dayjs(r.start_date).format("YYYY-MM-DD"),
      dayjs(r.end_date).format("YYYY-MM-DD"),
      r.type,
      r.leave_days,
      r.reason,
      r.status,
      dayjs(r.created_at).format("YYYY-MM-DD"),
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.join(","))
      .join("\n");
    const blob = new Blob(["\ufeff" + csvContent], {
      type: "text/csv;charset=utf-8",
    });
    saveAs(blob, `leave_requests_${dayjs().format("YYYYMMDD")}.csv`);
    message.success("CSV exported");
  };

  const exportToPDF = () => {
    if (!requests.length) return message.warning("No data to export");
    const doc = new jsPDF();
    doc.autoTable({
      head: [
        [
          "Employee",
          "Start Date",
          "End Date",
          "Type",
          "Days",
          "Reason",
          "Status",
          "Requested On",
        ],
      ],
      body: requests.map((r) => [
        `${r.firstname} ${r.lastname}`,
        dayjs(r.start_date).format("YYYY-MM-DD"),
        dayjs(r.end_date).format("YYYY-MM-DD"),
        r.type,
        r.leave_days,
        r.reason,
        r.status,
        dayjs(r.created_at).format("YYYY-MM-DD"),
      ]),
    });
    doc.save(`leave_requests_${dayjs().format("YYYYMMDD")}.pdf`);
    message.success("PDF exported");
  };

  return (
    <Card
      title="Leave Requests"
      extra={
        <Space>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 120 }}
          >
            <Option value="pending">Pending</Option>
            <Option value="approved">Approved</Option>
            <Option value="rejected">Rejected</Option>
          </Select>
          <Tooltip title="Export CSV">
            <Button icon={<FileExcelOutlined />} onClick={exportToCSV}>
              CSV
            </Button>
          </Tooltip>
          <Tooltip title="Export PDF">
            <Button icon={<FilePdfOutlined />} onClick={exportToPDF}>
              PDF
            </Button>
          </Tooltip>
        </Space>
      }
    >
      <Spin spinning={loading}>
        <Table
          size="small"
          columns={columns}
          dataSource={requests}
          rowKey="id"
          pagination={{ pageSize: 20, showSizeChanger: true }}
          scroll={{ x: "max-content" }}
        />
      </Spin>
    </Card>
  );
};

// Helper component to show leave balance
const BalanceInfo = ({ userId, requestDays }) => {
  const [balance, setBalance] = useState(null);
  const environment =
    window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;

  useEffect(() => {
    api
      .get(`/leave/balance/${userId}`)
      .then((res) => setBalance(res.data.leave_days))
      .catch((err) => console.error(err));
  }, [userId]);

  if (balance === null) return <span>Loading...</span>;
  const insufficient = requestDays > balance;
  return (
    <div style={{ color: insufficient ? "#ff4d4f" : "#52c41a" }}>
      {balance} days
      {insufficient && (
        <div style={{ fontSize: 12, color: "#ff4d4f" }}>
          Insufficient balance
        </div>
      )}
    </div>
  );
};

export default LeaveRequestsPage;
